import { Server } from 'lucide-react';
import { useMemo } from 'react';

import { IndexOptional } from '@/react/kubernetes/configs/types';
import { createStore } from '@/react/kubernetes/datatables/default-kube-datatable-store';
import { useEnvironment } from '@/react/portainer/environments/queries';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { Datatable } from '@@/datatables';
import { useTableState } from '@@/datatables/useTableState';

import { ApplicationPod } from '../ListView/ApplicationsDatatable/types';

import { ContainerRowData } from './ApplicationContainersDatatable/types';
import { getColumns } from './ApplicationContainersDatatable/columns';

const storageKey = 'k8sCustomResourcePodsDatatable';
const settingsStore = createStore(storageKey);

export function CustomResourcePodsDatatable({
  pods,
}: {
  pods: ApplicationPod[];
}) {
  const environmentId = useEnvironmentId();
  const useServerMetricsQuery = useEnvironment(
    environmentId,
    (env) => !!env?.Kubernetes?.Configuration.UseServerMetrics
  );
  const tableState = useTableState(settingsStore, storageKey);

  const containerRowData = useCustomResourcePodRowData(pods);

  return (
    <Datatable<IndexOptional<ContainerRowData>>
      dataset={containerRowData}
      columns={getColumns(!!useServerMetricsQuery.data)}
      settingsManager={tableState}
      isLoading={useServerMetricsQuery.isLoading}
      title="Custom Resource pods"
      titleIcon={Server}
      getRowId={(row) => row.podName}
      disableSelect
      data-cy="k8s-custom-resource-containers-datatable"
    />
  );
}

// Transform ApplicationPod[] into ContainerRowData[]
function useCustomResourcePodRowData(
  pods: ApplicationPod[]
): ContainerRowData[] {
  return useMemo(
    () =>
      pods.map((pod) => ({
        name: pod.ContainerName,
        image: pod.Image,
        imagePullPolicy: pod.ImagePullPolicy,
        podName: pod.Name,
        nodeName: pod.NodeName,
        podIp: pod.PodIP,
        creationDate: pod.CreationDate,
        status: pod.Status,
      })),
    [pods]
  );
}
